import { GUARD_RULE_IDS, type GuardDecisionDTO, type GuardDecisionVerdict } from "@roomate/core";

/*
 * The command guard's web side (docs/specs/command-guard.md §4.5): how a `[guard]` run note reads,
 * and how the project Guard tab filters its rows. Pure, so both views and the live feed agree.
 */

export const GUARD_VERDICTS: GuardDecisionVerdict[] = ["block", "ask", "allow", "grant-used"];
/** The Guard tab opens on the rows a person may need to act on. */
export const GUARD_DEFAULT_VERDICTS: GuardDecisionVerdict[] = ["block", "ask"];
export const GUARD_RULES: readonly string[] = GUARD_RULE_IDS;

export const GUARD_VERDICT_LABEL: Record<GuardDecisionVerdict, string> = {
  allow: "Allowed",
  block: "Blocked",
  ask: "Asked",
  "grant-used": "Granted",
};

/** The evidence label the hub puts on a guard decision, carrying the command's hash (docs/specs/command-guard.md §4.4). */
export const GUARD_HASH_LABEL = "guardHash";

const NOTE_PREFIX = "[guard] ";
const NOTE_SEPARATOR = " — ";

export type GuardNoteView = {
  verdict: "block" | "ask";
  /** `audit` mode: the verdict was logged but the command ran. */
  audit: boolean;
  rules: string[];
  /** Null when the note was cut before the command (the 2000-char note cap). */
  command: string | null;
  reason: string | null;
  decisionId: string | null;
};

/**
 * A `note` run line written for a block or ask. The hub's note text is
 * `[guard] <verdict> <rules> — <command> — <reason> — decision <id>`; the structured fields come from
 * `data`, and the command and reason come from the text. When the command itself contains " — ", the
 * reason is taken after the last separator, so a long command keeps its full text.
 */
export function parseGuardNote(e: { kind: string; text: string; data: unknown }): GuardNoteView | null {
  if (e.kind !== "note" || !e.text.startsWith(NOTE_PREFIX)) return null;
  const data = (e.data && typeof e.data === "object" ? e.data : {}) as Record<string, unknown>;
  const head = /^\[guard\] (?:\(audit, allowed; would (block|ask)\)|(block|ask)) /.exec(e.text);
  const fromText = head?.[1] ?? head?.[2];
  const verdict = data.verdict === "ask" || data.verdict === "block" ? data.verdict : fromText === "ask" ? "ask" : "block";
  const rules = Array.isArray(data.rules) ? data.rules.filter((r): r is string => typeof r === "string") : [];
  const decisionId = typeof data.decisionId === "string" && data.decisionId ? data.decisionId : null;

  const sep = e.text.indexOf(NOTE_SEPARATOR);
  if (sep < 0) return { verdict, audit: data.mode === "audit", rules, command: null, reason: null, decisionId };
  let rest = e.text.slice(sep + NOTE_SEPARATOR.length);
  const suffix = decisionId ? `${NOTE_SEPARATOR}decision ${decisionId}` : null;
  if (suffix && rest.endsWith(suffix)) rest = rest.slice(0, -suffix.length);
  const cut = rest.lastIndexOf(NOTE_SEPARATOR);
  const command = cut < 0 ? rest : rest.slice(0, cut);
  const reason = cut < 0 ? null : rest.slice(cut + NOTE_SEPARATOR.length);
  return { verdict, audit: data.mode === "audit", rules, command: command || null, reason: reason || null, decisionId };
}

export type GuardFilters = {
  agent?: string;
  verdicts: GuardDecisionVerdict[];
  rule?: string;
};

/** The query string values for `GET /api/projects/:id/guard` (`verdict` is a comma list). */
export function guardQuery(f: GuardFilters): { agent?: string; verdict?: string; rule?: string } {
  return {
    agent: f.agent || undefined,
    verdict: f.verdicts.length ? f.verdicts.join(",") : undefined,
    rule: f.rule || undefined,
  };
}

/**
 * The same filter applied to rows already in hand. The server applies it too; repeating it here
 * means a row that does not match the chosen filters never shows, even on a live update.
 */
export function matchesGuardFilters(row: Pick<GuardDecisionDTO, "agentId" | "verdict" | "rules">, f: GuardFilters): boolean {
  if (f.agent && row.agentId !== f.agent) return false;
  if (!f.verdicts.includes(row.verdict)) return false;
  if (f.rule && !row.rules.includes(f.rule)) return false;
  return true;
}

/** Toggling a verdict on or off; the last one cannot be switched off, since "no verdicts" is not a view. */
export function toggleVerdict(current: GuardDecisionVerdict[], v: GuardDecisionVerdict): GuardDecisionVerdict[] {
  if (current.includes(v)) return current.length === 1 ? current : current.filter((x) => x !== v);
  return GUARD_VERDICTS.filter((x) => x === v || current.includes(x));
}

/** Live events that can change the Guard tab: a block, an ask or a grant, or anything else under `guard.`. */
export function isGuardEvent(type: string): boolean {
  return type.startsWith("guard.");
}
